import type { RemoteExtensionsConfiguration } from 'src/gql/graphql';
import PublicEnv from 'shared/utils/public-env';

const LIVE_PREVIEW_SCOPE = 'customer_themes';

// Hosts allowed for manifest_url in production; anything else is rejected
// to prevent loading arbitrary remote code via this parameter.
const PRODUCTION_MANIFEST_HOST_ALLOWLIST: RegExp[] = [/^localhost$/, /^127\.0\.0\.1$/, /(^|\.)dexus\.io$/];

function isAllowedProductionManifestHost(hostname: string): boolean {
  return PRODUCTION_MANIFEST_HOST_ALLOWLIST.some((pattern) => pattern.test(hostname));
}

// Hosts allowed for the Website Theme's own `extensions.manifestUrl` in production. This field
// is written through `saveCmsWebsiteTheme`, whose worker-side authorization is `requireUser()` +
// `authorizeEnterprise()` (any admin of the enterprise) -- NOT gated to Dutchie superadmins the
// way the sibling dispensary-level `remoteExtensions.theme.manifestUrl` is
// (`RemoteExtensionsSettingsPolicy#update?` => `@user.super_admin?`, apps/armageddon). Unlike
// `PRODUCTION_MANIFEST_HOST_ALLOWLIST` above (internal dev/preview hosts for the query-string
// live-preview override), this is the real customer-facing CDN origin legitimate theme manifests
// are published under -- confirmed against the dispensary Extensions form's own default
// (`https://themes.dutchie.com/mf-manifest.json`, apps/admin/.../extensions/use-extensions-form.ts).
//
// Deliberately the EXACT host, not a `*.dutchie.com` wildcard: `dutchie.com` has other
// subdomains that serve customer/enterprise-controlled content (e.g. `images.dutchie.com`, an
// Imgix-fronted asset host for uploaded images -- confirmed via `shared/components/imgix` usage
// across apps/marketplace). A wildcard would admit exactly the untrusted-content risk this check
// exists to close. If a second legitimate manifest host is ever needed, add it explicitly here,
// one confirmed host at a time -- don't widen the pattern.
//
// Restricting to it closes the arbitrary-remote-code-execution vector a non-superadmin enterprise
// admin would otherwise have by pointing `manifestUrl` at a server they control:
// `useLazyRemoteModule` fetches and runs whatever JS is there, unsandboxed, wired to the commerce
// bridge, on every shopper's session on that storefront -- and inside a Dutchie superadmin's own
// authenticated session the next time they open that enterprise's CMS editor (`UI.websiteTheme`
// is fed from the unsaved editor draft too, not only the saved value). See
// `theme/provider/commerce-components/remote-theme.tsx`'s `websiteThemeIsLoadable`.
const WEBSITE_THEME_MANIFEST_HOST_ALLOWLIST: RegExp[] = [/^themes\.dutchie\.com$/];

/**
 * Validates a Website Theme `extensions.manifestUrl` before it's trusted to load as Module
 * Federation JavaScript. Requires a well-formed http(s) URL (rejects `javascript:`/`data:` etc.)
 * and, in production only, restricts the host to Dutchie's own customer-themes CDN -- see
 * `WEBSITE_THEME_MANIFEST_HOST_ALLOWLIST`'s own doc comment for why this field needs a stricter
 * check than the dispensary-level config it's layered on top of. Non-production environments
 * allow any http(s) host, matching this file's existing live-preview behavior, for local/staging
 * theme development against a non-dutchie.com manifest host.
 */
export function isAllowedWebsiteThemeManifestUrl(manifestUrl: string): boolean {
  try {
    const url = new URL(manifestUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return false;
    if (PublicEnv.appEnv !== 'production') return true;
    return WEBSITE_THEME_MANIFEST_HOST_ALLOWLIST.some((pattern) => pattern.test(url.hostname));
  } catch {
    return false;
  }
}

/**
 * Reads `manifest_url` and `theme` query params to build a live preview config for module federation.
 *
 * In production, `manifest_url` is restricted to localhost and `*.dexus.io` hosts
 * to prevent executing attacker-controlled JS in the authenticated production
 * page context. Non-production environments allow any http(s) host.
 */
export function getLivePreviewConfig(searchString?: string): RemoteExtensionsConfiguration | null {
  const search = searchString ?? (typeof window !== 'undefined' ? window.location.search : '');
  if (!search) return null;

  const params = new URLSearchParams(search.startsWith('?') ? search : `?${search}`);
  const manifestUrl = params.get('manifest_url');
  const theme = params.get('theme');

  if (!manifestUrl || !theme) return null;

  // Require a well-formed http(s) URL so a javascript:/data: URL isn't passed
  // to Module Federation.
  try {
    const url = new URL(manifestUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return null;

    if (PublicEnv.appEnv === 'production' && !isAllowedProductionManifestHost(url.hostname)) {
      return null;
    }
  } catch {
    return null;
  }

  return {
    manifestUrl,
    modulePath: `themes/${theme}`,
    scope: LIVE_PREVIEW_SCOPE,
  };
}
